import { Runtime } from "@morphism/fp";
import React from "react";
import { Human } from "~/Human";
import { ID } from "~/ID";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { useContext } from "./Context";

export type User = { readonly user: ID & Partial<Human.Name> };
export function User({ user }: User) {
  return user?.name ? <Human.Name name={user.name} /> : <>User {user.id}</>;
}

export namespace User {
  export const use = (): ID & Remote<User> => {
    const { identity } = useContext();
    const id = `${identity.userId}`;
    return pipe(Remote.Query.use(["user", id], request({ id })), (remote) => ({
      id,
      ...remote,
    }));
  };

  export const request = ({ id }: ID) =>
    flow(
      Request.fromConfig({ endpoint: `/users/${id}` }),
      Request.Expect.status(200, 404),
      Request.Expect.json(Response.runtime()),
      Request.onSuccess(Response.decode)
    );

  export const response = () =>
    Runtime.union([
      Runtime.strict({ message: Runtime.string() }),
      Runtime.strict({
        is_driver: Runtime.nullable(Runtime.boolean()),
        user_id: Runtime.number(),
        is_member_of_company: Runtime.boolean(),
      }),
    ]);

  export namespace Response {
    export const runtime = () =>
      Runtime.intersection([
        Human.Name.Response.runtime(),
        Runtime.strict({ user_id: Runtime.number() }),
      ]);

    export const decode = (response: Runtime.ToType<typeof runtime>): User => ({
      user: {
        ...ID.from(response.user_id),
        ...Human.Name.Response.decode(response),
      },
    });
  }
}
